import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import About from "@/components/About";
import WhyUs from "@/components/WhyUs";
import Testimonials from "@/components/Testimonials";
import Contact from "@/components/Contact";
import { motion } from "framer-motion";
import { Star, ShieldCheck, Clock, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QuoteModal } from "@/components/QuoteModal";

export default function UeberUns() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const location = useLocation();

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <SEOHead 
        title="Über uns - Easy Umzug"
        description="Dein zuverlässiger Partner für Umzüge, Möbelmontage und mehr. Wir stehen für Qualität, Pünktlichkeit und echte Kundenzufriedenheit."
        canonical={location.pathname}
      />
      <Navigation />
      <main className="pt-[88px]">
        <section className="py-20 md:py-32 bg-neutral-950 text-white text-center">
          <div className="container mx-auto px-4 flex flex-col items-center">
            <span className="inline-block font-semibold tracking-wider text-primary mb-4 uppercase text-sm md:text-base">
              Lerne uns kennen
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 max-w-3xl mx-auto">
              Über Easy Umzug
            </h1>
            <p className="text-lg md:text-xl text-neutral-400 max-w-2xl mx-auto font-medium mb-8">
              Dein zuverlässiger Partner für Umzüge, Möbelmontage und mehr. Wir stehen für Qualität, Pünktlichkeit und echte Kundenzufriedenheit.
            </p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
            >
              <QuoteModal>
                <Button size="lg" className="w-full sm:w-auto">
                  Kostenloses Angebot anfordern
                </Button>
              </QuoteModal>
              <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto">
                <a href="tel:+4915888640762">
                  <Phone className="w-5 h-5 mr-2" />
                  +49 15888 640762
                </a>
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap items-center justify-center gap-6 text-sm md:text-base text-neutral-200"
            >
              <div className="flex items-center gap-2">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <span className="font-medium">100% Kundenzufriedenheit</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <span className="font-medium">Voll versichert</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <span className="font-medium">Schnell & Zuverlässig</span>
              </div>
            </motion.div>
          </div>
        </section>
        
        <About />
        
        {/* Werte / Expertise Section */}
        <section className="py-20 bg-neutral-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="font-semibold text-primary uppercase tracking-wider text-sm">Unsere Philosophie</span>
              <h2 className="font-heading text-3xl md:text-4xl mt-2 mb-6">Was uns antreibt</h2>
              <p className="text-lg text-neutral-600 font-medium">
                Als lokales Unternehmen aus der Region legen wir größten Wert auf Verlässlichkeit, Transparenz und höchste Qualität bei jedem Handgriff.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { title: "Höchste Sorgfalt", desc: "Wir behandeln dein Eigentum, als wäre es unser eigenes. Jeder Karton und jedes Möbelstück wird fachgerecht gesichert." },
                { title: "Transparente Preise", desc: "Keine versteckten Kosten. Wir erstellen dir ein faires, klares Angebot, auf das du dich zu 100% verlassen kannst." },
                { title: "Erfahrenes Team", desc: "Unsere Mitarbeiter sind bestens geschult und bringen jahrelange Erfahrung in Umzug, Montage und Installation mit." }
              ].map((wert, i) => (
                <div key={i} className="bg-white p-8 rounded-xl shadow-sm border border-neutral-100">
                  <div className="w-12 h-12 bg-primary/10 text-primary flex items-center justify-center rounded-lg mb-6 font-bold text-xl">
                    {i + 1}
                  </div>
                  <h3 className="font-heading text-xl mb-3">{wert.title}</h3>
                  <p className="text-neutral-600 font-medium">{wert.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <WhyUs />
        <Testimonials />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
